export const fc = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
export const f0 = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
export const fp = (n) => (+n).toFixed(2) + '%';
export const ord = (d) => {
  // 11, 12, 13 are always "th" — they're exceptions to the 1st/2nd/3rd rule
  if (d >= 11 && d <= 13) return d + 'th';
  const s = ['th', 'st', 'nd', 'rd'];
  return d + (s[d % 10 > 3 ? 0 : d % 10] || 'th');
};
export const fdate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
export const MOS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const VCATS = [
  { id: 'gas', name: 'Gas', em: '⛽', col: '#ffd93d' },
  { id: 'groc', name: 'Groceries', em: '🛒', col: '#6bcb77' },
  { id: 'food', name: 'Food & Dining', em: '🍔', col: '#ff6b6b' },
  { id: 'ent', name: 'Entertainment', em: '🎮', col: '#4d96ff' },
  { id: 'shop', name: 'Shopping', em: '🛍️', col: '#ff6bdf' },
  { id: 'care', name: 'Personal Care', em: '💇', col: '#7C6FF7' },
  { id: 'oth', name: 'Other', em: '📦', col: '#a0aec0' },
];

// Budget slider helpers — slider 0-100
// Slider represents % of free money (after bills + savings) allocated to discretionary spending

export function getBudgetMode(slider) {
  if (slider === 0)
    return {
      name: 'No Fun Budget',
      color: 'var(--mut)',
      emoji: '💰',
      desc: 'All free money goes toward savings & debt',
    };
  if (slider <= 25)
    return {
      name: 'Aggressive Saver',
      color: 'var(--grn)',
      emoji: '🏦',
      desc: `${slider}% of free money for discretionary spending`,
    };
  if (slider <= 50)
    return {
      name: 'Conservative',
      color: '#6bcb77',
      emoji: '⚖️',
      desc: `${slider}% of free money for discretionary spending`,
    };
  if (slider <= 75)
    return {
      name: 'Balanced',
      color: 'var(--ylw)',
      emoji: '😌',
      desc: `${slider}% of free money for discretionary spending`,
    };
  return {
    name: 'Generous',
    color: 'var(--grn)',
    emoji: '🎉',
    desc: `${slider}% of free money for discretionary spending`,
  };
}

// Returns the dollar budget for discretionary spending this month
export function getVarBudget(income, allBills, savContrib, slider) {
  const totalFixed = allBills.reduce((s, b) => s + b.amt, 0);
  const available = Math.max(0, income - totalFixed - (savContrib || 0));
  const budget = Math.round(available * (slider / 100));
  return { budget, available };
}

export function getAllBills(cards, fixedBills, subscriptions) {
  const bills = (fixedBills || []).map((b) => ({ ...b }));
  (cards || []).forEach((c) =>
    bills.push({ id: c.id, name: c.name, amt: c.min, due: c.due, col: c.col })
  );
  (subscriptions || [])
    .filter((s) => s.cycle !== 'annual')
    .forEach((s) => {
      bills.push({ id: s.id, name: s.name, amt: s.amt, due: s.due || 1, col: s.col, isSub: true });
    });
  return bills.sort((a, b) => a.due - b.due);
}

export const mk = (y, m) => y + '-' + m;
export const isNowMo = (y, m) => {
  const n = new Date();
  return y === n.getFullYear() && m === n.getMonth();
};

/**
 * moIncome — sum income entries for a given month.
 * incomeLog is a flat array of {id, amt, date:"YYYY-MM-DD", sourceId?}.
 * key is mk()-format "YYYY-M" (0-indexed month, e.g. "2026-3" = April).
 */
export function moIncome(incomeLog, key) {
  const [year, month] = (key || '').split('-').map(Number);
  return (incomeLog || [])
    .filter((e) => {
      if (!e.date) return false;
      const parts = e.date.split('-').map(Number);
      return parts[0] === year && parts[1] - 1 === month; // parts[1] is 1-indexed
    })
    .reduce((s, e) => s + (e.amt || 0), 0);
}
export function moPaid(paidBills, key, cards, fixedBills, subscriptions) {
  const p = paidBills[key] || {};
  return getAllBills(cards, fixedBills, subscriptions).reduce((s, b) => {
    const v = p[b.id];
    if (!v) return s;
    return s + (typeof v === 'number' ? v : b.amt);
  }, 0);
}
export function catTotal(items) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((s, x) => s + x.amt, 0);
}
export function moVar(varSpend, key) {
  const vd = varSpend[key] || {};
  return Object.keys(vd).reduce((s, c) => s + catTotal(vd[c]), 0);
}
export function moSavings(savDeposits, key, savContrib) {
  const deps = savDeposits?.[key];
  if (Array.isArray(deps) && deps.length) return deps.reduce((s, x) => s + x.amt, 0);
  return 0;
}

export function getAllCats(customCats, disabledCats) {
  const off = disabledCats || [];
  return [...VCATS.filter((c) => !off.includes(c.id)), ...(customCats || [])];
}

export function getBudgetStreak(
  incomeLog,
  varSpend,
  savDeposits,
  cards,
  fixedBills,
  savContrib,
  budgetSlider,
  incomeMode,
  monthlyIncome,
  subscriptions
) {
  let streak = 0;
  const now = new Date();
  const allBills = getAllBills(cards, fixedBills, subscriptions || []);
  // Look back up to 24 months for long-term users
  for (let i = 1; i <= 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = mk(d.getFullYear(), d.getMonth());
    const varAmt = moVar(varSpend, k);
    const inc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog, k);
    if (!inc) break;
    const savA = moSavings(savDeposits, k, savContrib);
    const { budget } = getVarBudget(inc, allBills, savA, budgetSlider ?? 50);
    if (budget > 0 && varAmt <= budget) streak++;
    else break;
  }
  return streak;
}

export function getSavingsStreak(savDeposits) {
  let streak = 0;
  const now = new Date();
  for (let i = 1; i <= 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = mk(d.getFullYear(), d.getMonth());
    const deps = savDeposits?.[k];
    if (Array.isArray(deps) && deps.length > 0) streak++;
    else break;
  }
  return streak;
}

export function getAnnualSummary(
  incomeLog,
  paidBills,
  varSpend,
  savDeposits,
  cards,
  fixedBills,
  savContrib,
  year,
  incomeMode,
  monthlyIncome,
  subscriptions = []
) {
  let income = 0,
    billsPaid = 0,
    variable = 0,
    savings = 0;
  const now = new Date();
  const maxMonth = year === now.getFullYear() ? now.getMonth() : 11;
  for (let m = 0; m <= maxMonth; m++) {
    const k = mk(year, m);
    income += incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog, k);
    billsPaid += moPaid(paidBills, k, cards, fixedBills, subscriptions);
    variable += moVar(varSpend, k);
    savings += moSavings(savDeposits, k, savContrib);
  }
  return { income, billsPaid, variable, savings };
}

export function getFrequentPlaces(varSpend, catId, numMonths = 3) {
  const now = new Date();
  const counts = {};
  for (let i = 0; i < numMonths; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = mk(d.getFullYear(), d.getMonth());
    (varSpend[k]?.[catId] || []).forEach((it) => {
      counts[it.place] = (counts[it.place] || 0) + 1;
    });
  }
  return Object.entries(counts)
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([p]) => p);
}

export function getSpendingTrends(varSpend, numMonths = 6, cats = VCATS) {
  const now = new Date();
  return Array.from({ length: numMonths }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (numMonths - 1 - i), 1);
    const k = mk(d.getFullYear(), d.getMonth());
    const vd = varSpend[k] || {};
    const row = { lbl: MOS[d.getMonth()] };
    cats.forEach((cat) => {
      row[cat.id] = catTotal(vd[cat.id] || []);
    });
    row.total = cats.reduce((s, cat) => s + row[cat.id], 0);
    return row;
  });
}

export function getNetWorthHistory(savLog, checkins, cards, extraGoals) {
  const savMap = {};
  [...(savLog || [])].reverse().forEach((e) => {
    savMap[e.mo] = e.bal;
  });
  let lastSav = 0;
  const points = [...(checkins || [])]
    .sort((a, b) => a.ts - b.ts)
    .map((ci) => {
      const debt = Object.values(ci.actual || {}).reduce((s, v) => s + (v || 0), 0);
      if (savMap[ci.mo] !== undefined) lastSav = savMap[ci.mo];
      return { mo: ci.mo, netWorth: lastSav - debt };
    });
  const now = new Date();
  const curMo = MOS[now.getMonth()] + " '" + String(now.getFullYear()).slice(2);
  const curDebt = (cards || []).reduce((s, c) => s + +c.bal, 0);
  // Prefer extraGoals total (newer system) over legacy savLog snapshot for current point
  const extraGoalsTotal = (extraGoals || []).reduce((s, g) => s + (g.bal || 0), 0);
  const curSav = extraGoalsTotal > 0 ? extraGoalsTotal : ((savLog || [])[0]?.bal || 0);
  if ((curDebt > 0 || curSav > 0) && (!points.length || points[points.length - 1].mo !== curMo))
    points.push({ mo: curMo, netWorth: curSav - curDebt });
  return points;
}

export function getHealthScore(state) {
  const {
    cards,
    initCards,
    fixedBills,
    incomeLog,
    varSpend,
    savDeposits,
    savContrib,
    savLog,
    extraGoals,
    budgetSlider,
    incomeMode,
    monthlyIncome,
  } = state;
  const now = new Date();
  const allB = getAllBills(cards || [], fixedBills || [], state.subscriptions || []);

  // Budget adherence (0–25): last 3 months
  let budgetMonths = 0,
    budgetGood = 0;
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const kk = mk(d.getFullYear(), d.getMonth());
    const inc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog || [], kk);
    if (!inc) continue;
    budgetMonths++;
    const savA = moSavings(savDeposits || {}, kk, savContrib || 0);
    const { budget } = getVarBudget(inc, allB, savA, budgetSlider ?? 50);
    if (budget > 0 && moVar(varSpend || {}, kk) <= budget) budgetGood++;
  }
  const budgetScore = budgetMonths > 0 ? Math.round((budgetGood / budgetMonths) * 25) : 12;

  // Savings rate (0–25): savContrib / this month's income
  const km = mk(now.getFullYear(), now.getMonth());
  const incNow = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog || [], km);
  const savRate = incNow > 0 ? ((savContrib || 0) / incNow) * 100 : 0;
  const savScore = Math.min(25, Math.round((savRate / 20) * 25));

  // Emergency fund (0–25): months of expenses covered
  // Prefer extraGoals total (newer system) over legacy savLog snapshot
  const extraGoalsTotal = (extraGoals || []).reduce((s, g) => s + (g.bal || 0), 0);
  const lat = extraGoalsTotal > 0 ? extraGoalsTotal : ((savLog || [])[0]?.bal || 0);
  const monthlyExp = allB.reduce((s, b) => s + b.amt, 0) + (savContrib || 0);
  const efMonths = lat > 0 && monthlyExp > 0 ? lat / monthlyExp : 0;
  const efScore = Math.min(25, Math.round((efMonths / 6) * 25));

  // Debt progress (0–25)
  let debtScore = 25;
  if ((cards || []).length > 0) {
    const totalInit = (initCards || cards).reduce((s, c) => s + +c.bal, 0);
    const totalCur = (cards || []).reduce((s, c) => s + +c.bal, 0);
    const pct = totalInit > 0 ? Math.max(0, (totalInit - totalCur) / totalInit) : 0;
    debtScore = Math.round(pct * 25);
  }

  return {
    score: Math.min(100, budgetScore + savScore + efScore + debtScore),
    budgetScore,
    savScore,
    efScore,
    debtScore,
  };
}

export const MILESTONES = [
  {
    id: 'first_log',
    name: 'First Steps',
    em: '👣',
    desc: 'Log your first paycheck',
    check: (s) => (s.incomeLog || []).length > 0,
  },
  {
    id: 'budget_1mo',
    name: 'Budget Month',
    em: '✅',
    desc: 'Spend within budget for a month',
    check: (s) =>
      getBudgetStreak(
        s.incomeLog, s.varSpend, s.savDeposits,
        s.cards || [], s.fixedBills || [], s.savContrib,
        s.budgetSlider, s.incomeMode, s.monthlyIncome, s.subscriptions || []
      ) >= 1,
  },
  {
    id: 'budget_3mo',
    name: 'Budget Streak x3',
    em: '🔥',
    desc: 'Stay under budget 3 months in a row',
    check: (s) =>
      getBudgetStreak(
        s.incomeLog, s.varSpend, s.savDeposits,
        s.cards || [], s.fixedBills || [], s.savContrib,
        s.budgetSlider, s.incomeMode, s.monthlyIncome, s.subscriptions || []
      ) >= 3,
  },
  {
    id: 'savings_100',
    name: '$100 Saved',
    em: '🐷',
    desc: 'Save your first $100',
    check: (s) => ((s.savLog || [])[0]?.bal || 0) >= 100,
  },
  {
    id: 'savings_1k',
    name: '$1,000 Saved',
    em: '🏦',
    desc: 'Reach $1,000 in savings',
    check: (s) => ((s.savLog || [])[0]?.bal || 0) >= 1000,
  },
  {
    id: 'savings_5k',
    name: '$5,000 Saved',
    em: '💎',
    desc: 'Reach $5,000 in savings',
    check: (s) => ((s.savLog || [])[0]?.bal || 0) >= 5000,
  },
  {
    id: 'debt_25',
    name: '25% Paid Down',
    em: '📉',
    desc: 'Pay off 25% of starting debt',
    check: (s) => {
      const ti = (s.initCards || []).reduce((a, c) => a + +c.bal, 0);
      const tc = (s.cards || []).reduce((a, c) => a + +c.bal, 0);
      return ti > 0 && (ti - tc) / ti >= 0.25;
    },
  },
  {
    id: 'debt_half',
    name: 'Halfway There!',
    em: '⚡',
    desc: 'Pay off 50% of starting debt',
    check: (s) => {
      const ti = (s.initCards || []).reduce((a, c) => a + +c.bal, 0);
      const tc = (s.cards || []).reduce((a, c) => a + +c.bal, 0);
      return ti > 0 && (ti - tc) / ti >= 0.5;
    },
  },
  {
    id: 'debt_free',
    name: 'Debt Free!',
    em: '🏆',
    desc: 'Pay off all your debt',
    check: (s) => (s.cards || []).length > 0 && (s.cards || []).every((c) => +c.bal <= 0),
  },
];

export function getEarnedMilestones(state) {
  return MILESTONES.map((m) => ({ ...m, earned: m.check(state) }));
}

// ── XP / Level system ────────────────────────────────────────────────────────

export const LEVEL_THRESHOLDS = [
  0, 100, 300, 600, 1000, 1500, 2200, 3000, 4000, 5500,
  7500, 10000, 13000, 16500, 20500, 25000, 30000, 36000, 43000, 51000,
];

export const LEVEL_NAMES = [
  '', 'Beginner', 'Beginner', 'Beginner',
  'Apprentice', 'Apprentice', 'Apprentice',
  'Budgeter', 'Budgeter', 'Budgeter',
  'Saver', 'Saver', 'Saver',
  'Pro', 'Pro', 'Pro',
  'Expert', 'Expert', 'Expert',
  'Master', 'Legend',
];

export const LEVEL_UNLOCKS = {
  5: { label: 'Rank visible on leaderboard', em: '🏆' },
  10: { label: 'Midnight theme unlocked', em: '🌙' },
  15: { label: 'Clan creation unlocked', em: '⚔️' },
  20: { label: 'Legend badge unlocked', em: '👑' },
};

export function computeXP(state) {
  const {
    incomeLog, varSpend, savDeposits, savContrib, budgetSlider,
    incomeMode, monthlyIncome, cards, initCards, fixedBills, extraGoals, subscriptions,
  } = state;
  let xp = 0;
  const now = new Date();
  const allBills = getAllBills(cards || [], fixedBills || [], subscriptions || []);

  // Budget streak: 50 XP per consecutive month (1.5× hot-streak bonus at 3+)
  const streak = getBudgetStreak(
    incomeLog, varSpend, savDeposits, cards || [], fixedBills || [],
    savContrib, budgetSlider, incomeMode, monthlyIncome, subscriptions || []
  );
  xp += streak >= 3 ? Math.round(streak * 50 * 1.5) : streak * 50;

  // Savings streak: 30 XP per consecutive saving month
  xp += getSavingsStreak(savDeposits) * 30;

  // Historical budget adherence: 40 XP per good month tracked, 10 XP for tracking but over
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = mk(d.getFullYear(), d.getMonth());
    const varAmt = moVar(varSpend || {}, k);
    if (varAmt === 0) continue; // no tracking = no credit
    const inc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog || [], k);
    if (!inc) continue;
    const savA = moSavings(savDeposits || {}, k, savContrib || 0);
    const { budget } = getVarBudget(inc, allBills, savA, budgetSlider ?? 50);
    xp += (budget > 0 && varAmt <= budget) ? 40 : 10;
  }

  // Health score: up to 200 XP
  xp += Math.round(getHealthScore(state).score * 2);

  // Milestones: 100 XP each
  xp += MILESTONES.filter((m) => m.check(state)).length * 100;

  // Debt payoff progress: up to 300 XP
  const initTotal = (initCards || cards || []).reduce((s, c) => s + +c.bal, 0);
  const curTotal = (cards || []).reduce((s, c) => s + +c.bal, 0);
  if (initTotal > 0) xp += Math.round(((initTotal - curTotal) / initTotal) * 300);

  // Savings goals reached: 150 XP each
  xp += (extraGoals || []).filter((g) => g.target > 0 && g.bal >= g.target).length * 150;

  // Daily login XP: 5 XP per day tracked via loginDays
  if (state.loginDays) xp += Math.min(state.loginDays, 365) * 5;

  // No-spend streak: 10 XP per day
  xp += getNoSpendStreak(state) * 10;

  // Completed daily quests XP (sum across all days, 12 XP each)
  Object.values(state.completedQuests || {}).forEach(dayList => {
    xp += (dayList.length || 0) * 12;
  });

  // Autopilot paycheck steps checked off: 5 XP each
  Object.values(state.planDone || {}).forEach((ids) => {
    xp += (ids.length || 0) * 5;
  });

  // Referral bonus: 200 XP if claimed
  if (state.referralClaimed) xp += 200;

  return Math.max(0, xp);
}

/**
 * Compute total net worth: sum of all assets minus sum of all debt balances.
 * @param {object} state
 * @returns {{ assets: number, debts: number, netWorth: number }}
 */
export function getNetWorth(state) {
  const debts = (state.cards || []).reduce((s, c) => s + +c.bal, 0);
  const savingsTotal = Object.values(state.savDeposits || {}).flat().reduce((s, d) => s + (d.amt || 0), 0);
  const assetTotal = (state.assets || []).reduce((s, a) => s + +a.value, 0);
  return {
    assets: assetTotal + savingsTotal,
    debts,
    netWorth: assetTotal + savingsTotal - debts,
  };
}

/**
 * Compare this month's category spending vs last month.
 * Returns array of { cat, thisAmt, lastAmt, delta, pct } sorted by |delta| desc.
 * @param {object} state
 * @returns {Array}
 */
export function getBeatLastMonth(state) {
  const now = new Date();
  const thisK = mk(now.getFullYear(), now.getMonth());
  const lastD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastK = mk(lastD.getFullYear(), lastD.getMonth());
  const thisSpend = state.varSpend?.[thisK] || {};
  const lastSpend = state.varSpend?.[lastK] || {};
  const allCats = new Set([...Object.keys(thisSpend), ...Object.keys(lastSpend)]);
  return Array.from(allCats).map(cat => {
    // varSpend[moKey][catId] is an array of {place, amt, ts} — must use catTotal()
    const thisAmt = catTotal(thisSpend[cat] || []);
    const lastAmt = catTotal(lastSpend[cat] || []);
    const delta = thisAmt - lastAmt;
    const pct = lastAmt > 0 ? Math.round((delta / lastAmt) * 100) : null;
    return { cat, thisAmt, lastAmt, delta, pct };
  }).filter(r => r.thisAmt > 0 || r.lastAmt > 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}

/**
 * Detect bill price changes: compare each bill's current amount vs what was
 * actually paid last month. Returns array of { name, oldAmt, newAmt, delta }.
 * @param {object} state
 * @returns {Array}
 */
export function getBillPriceChanges(state) {
  const now = new Date();
  const lastD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastK = mk(lastD.getFullYear(), lastD.getMonth());
  const paid = state.paidBills || {};
  const changes = [];
  const allBills = [
    ...(state.fixedBills || []),
    ...(state.subscriptions || []).filter(s => s.cycle !== 'annual'),
  ];
  allBills.forEach(bill => {
    const lastPaid = paid[lastK]?.[bill.id];
    if (!lastPaid) return;
    const currentAmt = parseFloat(bill.amt) || 0;
    const paidAmt = typeof lastPaid === 'number' ? lastPaid : typeof lastPaid === 'object' ? parseFloat(lastPaid.amt) || 0 : 0;
    if (!paidAmt) return;
    if (Math.abs(currentAmt - paidAmt) >= 1) {
      changes.push({ name: bill.name, oldAmt: paidAmt, newAmt: currentAmt, delta: currentAmt - paidAmt });
    }
  });
  return changes;
}

/**
 * Net worth 1, 3 and 5 years out, from the month-by-month Autopilot
 * simulation in projectFinances.
 * @param {object} state
 * @returns {Array<{ label: string, months: number, value: number }>}
 */
export function getNetWorthTrajectory(state) {
  const proj = projectFinances(state, 60);
  if (!proj) return [];
  return [12, 36, 60].map((m) => ({
    label: m === 12 ? '1 yr' : m === 36 ? '3 yr' : '5 yr',
    months: m,
    value: Math.round(proj.points[m - 1].netWorth),
  }));
}

/**
 * Get current no-spend streak: consecutive days ending yesterday with no varSpend.
 * @param {object} state
 * @returns {number}
 */
export function getNoSpendStreak(state) {
  const noSpendDays = state.noSpendDays || [];
  if (!noSpendDays.length) return 0;
  let streak = 0;
  const today = new Date();
  for (let i = 1; i <= 365; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    if (noSpendDays.includes(ds)) streak++;
    else break;
  }
  return streak;
}

/**
 * Generate today's daily quests deterministically based on day-of-year seed.
 * Returns array of { id, label, em, xp, done } where done is computed from state.
 * @param {object} state
 * @returns {Array}
 */
export function getDailyQuests(state) {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const moKey = mk(now.getFullYear(), now.getMonth());
  const varSpendToday = Object.values(state.varSpend?.[moKey] || {}).flat()
    .some(e => e.date === today || (e.ts && new Date(e.ts).toISOString().slice(0, 10) === today));
  const isoMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const hasIncomeThisMonth = (state.incomeLog || []).some(e => e.date?.startsWith(isoMonth));
  const completed = state.completedQuests?.[today] || [];

  const ALL_QUESTS = [
    { id: 'log_spend',    label: 'Log at least one expense today',        em: '📝', xp: 15, done: varSpendToday },
    { id: 'no_spend',     label: 'Mark today as a no-spend day',          em: '🛑', xp: 25, done: (state.noSpendDays || []).includes(today) },
    { id: 'check_budget', label: 'Review your monthly budget',            em: '📊', xp: 10, done: completed.includes('check_budget') },
    { id: 'log_income',   label: 'Log your income for the month',         em: '💵', xp: 20, done: hasIncomeThisMonth },
    { id: 'check_subs',   label: 'Review your subscriptions list',        em: '📋', xp: 10, done: completed.includes('check_subs') },
    { id: 'update_bank',  label: 'Update your bank balance',              em: '🏦', xp: 15, done: state.bankBalance > 0 && state.bankBalanceDate === today },
    { id: 'check_nw',     label: 'Check your net worth tracker',          em: '💎', xp: 10, done: completed.includes('check_nw') },
    { id: 'social_post',  label: 'Post a tip or win to the community',    em: '🌐', xp: 20, done: completed.includes('social_post') },
  ];
  // Pick 3 quests deterministically by day seed
  const seed = now.getFullYear() * 1000 + Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const indices = [seed % 8, (seed + 3) % 8, (seed + 5) % 8];
  const unique = [...new Set(indices)];
  return unique.map(i => ALL_QUESTS[i]);
}

export function computeLevel(xp) {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i]) return Math.min(i + 1, LEVEL_THRESHOLDS.length);
  }
  return 1;
}

export function getLevelInfo(state) {
  const xp = computeXP(state);
  const level = computeLevel(xp);
  const idx = level - 1;
  const currentThreshold = LEVEL_THRESHOLDS[idx] || 0;
  const nextThreshold = LEVEL_THRESHOLDS[idx + 1] || currentThreshold + 10000;
  const xpInLevel = xp - currentThreshold;
  const xpNeeded = nextThreshold - currentThreshold;
  const pct = Math.min(100, Math.round((xpInLevel / xpNeeded) * 100));
  const name = LEVEL_NAMES[idx] || 'Legend';
  const nextUnlock = Object.entries(LEVEL_UNLOCKS)
    .map(([l, u]) => ({ ...u, level: parseInt(l) }))
    .find((u) => u.level > level) || null;
  return { level, name, xp, xpInLevel, xpNeeded, pct, nextUnlock };
}

// ── Financial Weather Forecast ────────────────────────────────────────────────

export function getWeatherForecast({ willGoNegative, isTight, budgetRem, dueSoon, _varBudget, _vari, bankBalance }) {
  if (willGoNegative || (bankBalance > 0 && bankBalance < 50))
    return { em: '⛈', label: 'Storm Warning', desc: 'Balance could go negative — review upcoming bills', col: 'var(--red)' };
  if (isTight)
    return { em: '🌧', label: 'Rough Ahead', desc: 'Balance will get tight soon', col: '#ff8c42' };
  if (budgetRem !== undefined && budgetRem < 0)
    return { em: '⛅', label: 'Getting Stretched', desc: 'Over your discretionary budget this month', col: 'var(--ylw)' };
  if (dueSoon && dueSoon.length > 0)
    return { em: '🌤', label: 'Some Clouds', desc: `${dueSoon.length} bill${dueSoon.length > 1 ? 's' : ''} due this week`, col: 'var(--ylw)' };
  return { em: '☀️', label: 'Clear Skies', desc: "You're in great financial shape", col: 'var(--grn)' };
}

export function calcPayoff(cards, extra, method, lump = 0) {
  if (!cards || !cards.length) return { sched: [], mos: 0, int: 0, end: '—', cpoff: {} };
  const now = new Date();
  const START_MO = now.getMonth();
  const START_YR = now.getFullYear();
  const pri = [...cards];
  if (method === 'snowball') pri.sort((a, b) => a.bal - b.bal);
  if (method === 'avalanche') pri.sort((a, b) => b.apr - a.apr);
  const bals = {};
  cards.forEach((c) => {
    bals[c.id] = +c.bal || 0;
  });
  if (lump > 0 && pri.length) bals[pri[0].id] = Math.max(0, bals[pri[0].id] - lump);
  const paidOff = {},
    sched = [];
  let freed = 0,
    cumInt = 0;
  for (let i = 0; i < 120; i++) {
    if (!cards.some((c) => (bals[c.id] || 0) > 0.01)) break;
    const mi = (START_MO + i) % 12,
      yr = START_YR + Math.floor((START_MO + i) / 12);
    const lbl = MOS[mi] + " '" + String(yr).slice(2);
    const row = { i, lbl };
    let moInt = 0;
    const tgt = pri.find((c) => !paidOff[c.id]);
    // Capture freed before the forEach so closures reference a stable value per iteration
    const freedAtStart = freed;
    // eslint-disable-next-line no-loop-func
    cards.forEach((c) => {
      if (paidOff[c.id]) {
        row[c.id] = 0;
        return;
      }
      const rate = c.apr / 100 / 12,
        int = (bals[c.id] || 0) * rate;
      moInt += int;
      let pay = +c.min || 0;
      if (method !== 'min' && tgt && tgt.id === c.id) pay += extra + freedAtStart;
      pay = Math.min(pay, (bals[c.id] || 0) + int);
      const nb = (bals[c.id] || 0) + int - pay;
      if (nb <= 0.01) {
        paidOff[c.id] = true;
        freed += +c.min || 0;
        bals[c.id] = 0;
      } else bals[c.id] = nb;
      row[c.id] = Math.round((bals[c.id] || 0) * 100) / 100;
    });
    cumInt += moInt;
    row.tot = Math.round(cards.reduce((s, c) => s + (bals[c.id] || 0), 0) * 100) / 100;
    row.mi = Math.round(moInt * 100) / 100;
    row.ci = Math.round(cumInt * 100) / 100;
    sched.push(row);
  }
  const cpoff = {};
  cards.forEach((c) => {
    const idx = sched.findIndex((r) => r[c.id] === 0);
    if (idx >= 0) {
      const mi2 = (START_MO + idx) % 12,
        yr2 = START_YR + Math.floor((START_MO + idx) / 12);
      cpoff[c.id] = { lbl: MOS[mi2] + " '" + String(yr2).slice(2), mo: idx + 1 };
    }
  });
  return {
    sched,
    mos: sched.length,
    int: Math.round(cumInt * 100) / 100,
    end: sched.length ? sched[sched.length - 1].lbl : '—',
    cpoff,
  };
}

export function getSpendingInsights(varSpend, customCats, numMonths = 3, disabledCats) {
  const cats = getAllCats(customCats, disabledCats);
  const now = new Date();
  const curK = mk(now.getFullYear(), now.getMonth());
  const curVd = varSpend[curK] || {};

  const sums = {};
  let monthsWithData = 0;
  for (let i = 1; i <= numMonths; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = mk(d.getFullYear(), d.getMonth());
    const vd = varSpend[k] || {};
    const total = cats.reduce((s, c) => s + catTotal(vd[c.id] || []), 0);
    if (total === 0) continue;
    monthsWithData++;
    cats.forEach((c) => {
      sums[c.id] = (sums[c.id] || 0) + catTotal(vd[c.id] || []);
    });
  }
  if (!monthsWithData) return null;

  const rows = cats
    .map((cat) => {
      const avg = Math.round((sums[cat.id] || 0) / monthsWithData);
      const cur = catTotal(curVd[cat.id] || []);
      const pct = avg > 0 ? Math.round(((cur - avg) / avg) * 100) : null;
      return { ...cat, avg, cur, pct };
    })
    .filter((c) => c.avg > 0 || c.cur > 0)
    .sort((a, b) => b.avg - a.avg);

  return { rows, monthsWithData };
}

export function getFinancialTips(state) {
  const {
    cards = [],
    extra = 0,
    method,
    savContrib = 0,
    savLog = [],
    fixedBills = [],
    incomeLog = [],
    incomeMode,
    monthlyIncome = 0,
  } = state;
  const now = new Date();
  const k = mk(now.getFullYear(), now.getMonth());
  const allBills = getAllBills(cards, fixedBills, state.subscriptions || []);
  const inc = incomeMode === 'monthly' ? monthlyIncome : moIncome(incomeLog, k);
  const totBal = cards.reduce((s, c) => s + +c.bal, 0);
  const lat = savLog[0]?.bal || 0;
  const monthlyExp = allBills.reduce((s, b) => s + b.amt, 0);
  const efMonths = lat > 0 && monthlyExp > 0 ? lat / monthlyExp : 0;
  const savRate = inc > 0 && savContrib > 0 ? (savContrib / inc) * 100 : 0;
  const tips = [];

  if (efMonths < 3 && monthlyExp > 0)
    tips.push({
      id: 'ef',
      em: '🛡️',
      color: 'var(--blu)',
      title: 'Build Your Emergency Fund',
      body: `Aim for 3–6 months of expenses (${f0(monthlyExp * 3)}). You have ${efMonths.toFixed(1)} months covered. Even $25/mo adds up faster than you think.`,
    });

  const highAPR = cards.slice().sort((a, b) => b.apr - a.apr)[0];
  if (highAPR && highAPR.apr > 15 && highAPR.bal > 0)
    tips.push({
      id: 'apr',
      em: '🔥',
      color: 'var(--red)',
      title: `${highAPR.name} Costs You Every Day`,
      body: `At ${highAPR.apr}% APR you're paying ~${fc((highAPR.bal * highAPR.apr) / 100 / 365)}/day in interest. Every extra dollar thrown at it stops that clock immediately.`,
    });

  if (inc > 0 && savRate < 10)
    tips.push({
      id: 'sav',
      em: '💡',
      color: 'var(--grn)',
      title: savRate === 0 ? 'Start Saving Something' : 'Boost Your Savings Rate',
      body:
        savRate === 0
          ? `Even 5% of income (${f0(inc * 0.05)}/mo) builds a powerful safety net. Start small, automate it, and raise it 1% every few months.`
          : `You're saving ${savRate.toFixed(1)}% of income. The 15–20% target feels big — try raising your contribution by just ${f0(inc * 0.01)}/mo next month.`,
    });

  if (method === 'snowball' && cards.length > 1) {
    const snowInt = calcPayoff(cards, extra, 'snowball').int;
    const avalInt = calcPayoff(cards, extra, 'avalanche').int;
    const diff = Math.round(snowInt - avalInt);
    if (diff > 150)
      tips.push({
        id: 'aval',
        em: '🏔️',
        color: 'var(--pnk)',
        title: 'Avalanche Could Save You More',
        body: `Switching from Snowball to Avalanche payoff could save you ${f0(diff)} in total interest. Snowball wins on motivation; Avalanche wins on math.`,
      });
  }

  if (totBal > 0 && lat > totBal * 0.25)
    tips.push({
      id: 'lump',
      em: '💸',
      color: 'var(--ylw)',
      title: 'Use a Lump Sum to Accelerate Payoff',
      body: `You have solid savings. Use the Lump Sum field in the Debt tab to see exactly how a one-time payment would slash your interest and finish date.`,
    });

  tips.push({
    id: 'compound',
    em: '📈',
    color: 'var(--grn)',
    title: 'Compound Interest Works Both Ways',
    body: `High-interest debt compounds against you daily. Once paid off, redirect those payments into an index fund — the same math that hurt you will start working for you.`,
  });

  tips.push({
    id: 'automate',
    em: '🤖',
    color: 'var(--mut)',
    title: 'Automate the Minimums',
    body: `Set up autopay for every bill and debt minimum so you never miss one. Late fees and credit dings cost far more than they look. Pay extra on top manually.`,
  });

  return tips.slice(0, 3);
}

export function svgLine(data, series, h = 190) {
  if (!data || !data.length) return '';
  const W = 840,
    ML = 54,
    MR = 12,
    MT = 10,
    MB = 28,
    CW = W - ML - MR,
    CH = h - MT - MB;
  const vals = data.flatMap((d) => series.map((s) => d[s.k] || 0)).filter((v) => !isNaN(v));
  const maxV = Math.max(...vals, 1);
  const xS = (i) => ML + (i / (data.length - 1 || 1)) * CW;
  const yS = (v) => MT + CH - (v / maxV) * CH;
  let svg =
    '<svg viewBox="0 0 ' +
    W +
    ' ' +
    h +
    '" width="100%" style="display:block;overflow:visible"><defs>';
  series
    .filter((s) => s.fill)
    .forEach((s) => {
      svg +=
        '<linearGradient id="g' +
        s.k +
        '" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="' +
        s.c +
        '" stop-opacity=".18"/><stop offset="100%" stop-color="' +
        s.c +
        '" stop-opacity=".01"/></linearGradient>';
    });
  svg += '</defs>';
  [0, 0.25, 0.5, 0.75, 1].forEach((t) => {
    const y = MT + CH * (1 - t),
      v = maxV * t;
    const lbl =
      v >= 1000 ? '$' + (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'k' : '$' + Math.round(v);
    svg +=
      '<line x1="' +
      ML +
      '" y1="' +
      y.toFixed(1) +
      '" x2="' +
      (ML + CW) +
      '" y2="' +
      y.toFixed(1) +
      '" stroke="#2e2e40" stroke-width="1"/>';
    svg +=
      '<text x="' +
      (ML - 5) +
      '" y="' +
      (y + 4).toFixed(1) +
      '" text-anchor="end" font-size="10" fill="#7a7a9a" font-family="DM Sans,sans-serif">' +
      lbl +
      '</text>';
  });
  series
    .filter((s) => s.fill)
    .forEach((s) => {
      const pts = data.map((d, i) => xS(i).toFixed(1) + ',' + yS(d[s.k] || 0).toFixed(1));
      svg +=
        '<path d="M ' +
        pts[0] +
        ' L ' +
        pts.join(' L ') +
        ' L ' +
        xS(data.length - 1).toFixed(1) +
        ',' +
        (MT + CH).toFixed(1) +
        ' L ' +
        xS(0).toFixed(1) +
        ',' +
        (MT + CH).toFixed(1) +
        ' Z" fill="url(#g' +
        s.k +
        ')"/>';
    });
  series.forEach((s) => {
    const pts = data.map((d, i) => xS(i).toFixed(1) + ',' + yS(d[s.k] || 0).toFixed(1)).join(' ');
    svg +=
      '<polyline points="' +
      pts +
      '" fill="none" stroke="' +
      s.c +
      '" stroke-width="' +
      (s.w || 2.5) +
      '" ' +
      (s.dash ? 'stroke-dasharray="6,3"' : '') +
      ' stroke-linecap="round" stroke-linejoin="round"/>';
  });
  const step = Math.max(1, Math.ceil(data.length / 7));
  data.forEach((d, i) => {
    if (i % step === 0 || i === data.length - 1)
      svg +=
        '<text x="' +
        xS(i).toFixed(1) +
        '" y="' +
        (MT + CH + 18).toFixed(1) +
        '" text-anchor="middle" font-size="10" fill="#7a7a9a" font-family="DM Sans,sans-serif">' +
        d.lbl +
        '</text>';
  });
  return svg + '</svg>';
}

// ── Shared income helpers ─────────────────────────────────────────────────────

export const INCOME_TYPES = [
  { id: 'job', label: 'Job / Salary', em: '💼' },
  { id: 'freelance', label: 'Freelance', em: '💻' },
  { id: 'sidegig', label: 'Side Gig', em: '⚡' },
  { id: 'rental', label: 'Rental Income', em: '🏠' },
  { id: 'benefits', label: 'Benefits', em: '🏥' },
  { id: 'other', label: 'Other', em: '💰' },
];

export const FREQ_LABELS = {
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  semimonthly: 'Twice a month',
  monthly: 'Monthly',
  annual: 'Annually',
};

export const FREQ_TO_MONTHLY = {
  weekly: 52 / 12,
  biweekly: 26 / 12,
  semimonthly: 2,
  monthly: 1,
  annual: 1 / 12,
};

export function srcMonthly(src) {
  return (parseFloat(src.amt) || 0) * (FREQ_TO_MONTHLY[src.freq] || 1);
}

export function sourcesToMonthly(sources) {
  return (sources || []).reduce((s, src) => s + srcMonthly(src), 0);
}

export function nextDueDate(day) {
  const now = new Date();
  let d = new Date(now.getFullYear(), now.getMonth(), day);
  if (d < now) d = new Date(now.getFullYear(), now.getMonth() + 1, day);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function dueDateForMonth(day, year, month) {
  return new Date(year, month, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Rough grocery cost multiplier by household size (1 person = 1.0)
const HOUSEHOLD_SCALE = [1, 1, 1.7, 2.3, 2.9, 3.4];
export const householdScale = (n) => HOUSEHOLD_SCALE[Math.min(5, Math.max(1, Math.round(+n || 1)))];

export function getCatSuggestions(varBudget, householdSize = 1) {
  if (!varBudget || varBudget <= 0) return {};
  const hh = householdScale(householdSize);
  const people = Math.min(5, Math.max(1, Math.round(+householdSize || 1)));
  const rules = {
    gas: { pct: 0.18, min: 80, max: 350, note: 'typical monthly gas' },
    groc: {
      pct: 0.35,
      min: 150 * hh,
      max: 600 * hh,
      note: `groceries for ${people === 1 ? '1 person' : people + (people === 5 ? '+' : '') + ' people'}`,
    },
    food: { pct: 0.2, min: 80, max: 400 * Math.sqrt(hh), note: 'eating out & takeout' },
    ent: { pct: 0.1, min: 40, max: 250, note: 'streaming, events, fun' },
    shop: { pct: 0.1, min: 40, max: 400, note: 'clothes, household items' },
    care: { pct: 0.05, min: 40, max: 150, note: 'haircuts, hygiene, health' },
    oth: { pct: 0.08, min: 20, max: 200, note: 'catch-all' },
  };
  const out = {};
  for (const [id, r] of Object.entries(rules)) {
    const raw = Math.round((varBudget * r.pct) / 5) * 5;
    out[id] = { amt: Math.round(Math.min(r.max, Math.max(r.min, raw)) / 5) * 5, note: r.note };
  }
  return out;
}

/**
 * Typical monthly spend per category from the user's own history: the median
 * of the last `numMonths` completed months that have any tracked spending.
 * Only categories with at least 2 such months are returned.
 * @returns {Object<string, { amt: number, months: number }>}
 */
export function getHistoryBudgets(varSpend, numMonths = 3, now = new Date()) {
  const months = [];
  for (let i = 1; i <= 12 && months.length < numMonths; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = mk(d.getFullYear(), d.getMonth());
    if (moVar(varSpend || {}, key) > 0) months.push((varSpend || {})[key] || {});
  }
  const cats = new Set(months.flatMap((m) => Object.keys(m)));
  const out = {};
  cats.forEach((id) => {
    const vals = months.map((m) => catTotal(m[id] || [])).sort((a, b) => a - b);
    const used = vals.filter((v) => v > 0).length;
    if (months.length < 2 || used === 0) return;
    const mid = Math.floor(vals.length / 2);
    const median = vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
    out[id] = { amt: Math.round(median / 5) * 5, months: months.length };
  });
  return out;
}

/**
 * Category budget suggestions for Autopilot: the user's own spending history
 * where there's enough of it, otherwise the rule-of-thumb suggestions scaled
 * to their household size.
 */
export function getSmartCatBudgets(state, varBudget) {
  const base = getCatSuggestions(varBudget, state.householdSize || 1);
  const hist = getHistoryBudgets(state.varSpend);
  const out = { ...base };
  Object.entries(hist).forEach(([id, h]) => {
    out[id] = { amt: h.amt, note: `your typical month (last ${h.months} months)`, fromHistory: true };
  });
  return out;
}

export function getCashFlow(state) {
  const { monthlyIncome, fixedBills, cards, subscriptions, catBudgets, savContrib, incomeLog } = state;
  const now = new Date();
  const loggedInc = moIncome(incomeLog, mk(now.getFullYear(), now.getMonth()));
  const inc = monthlyIncome || loggedInc || 0;
  const allBills = getAllBills(cards || [], fixedBills || [], subscriptions || []);
  const billsTotal = allBills.reduce((s, b) => s + b.amt, 0);
  const varTotal = Object.values(catBudgets || {}).reduce((s, v) => s + (v || 0), 0);
  const savTotal = savContrib || 0;
  const totalSpend = billsTotal + varTotal + savTotal;
  const leftOver = inc - totalSpend;
  return { inc, billsTotal, varTotal, savTotal, totalSpend, leftOver };
}

// ── Cash Flow Forecast — projected paydays + upcoming bills, day by day ──────
//
// Unlike getCashFlow (a flat monthly total), this projects *when* money moves:
// future paydays from Income Sources (using their pay frequency + optional
// firstPayMs anchor) interleaved with upcoming bill due dates, so a running
// balance can be shown starting from the user's current bank balance.

/**
 * projectIncomeEvents — expand recurring Income Sources into dated paycheck
 * events within [startDate, endDate]. 'annual' income (e.g. a yearly bonus)
 * has no captured date and is intentionally left out of the day-by-day
 * timeline — it still counts toward the monthly income total elsewhere.
 */
export function projectIncomeEvents(incomeSources, startDate, endDate) {
  const oneDay = 86400000;
  const events = [];

  (incomeSources || []).forEach((src) => {
    const amt = parseFloat(src.amt) || 0;
    if (amt <= 0) return;

    const push = (d) => {
      if (d >= startDate && d <= endDate) {
        events.push({ date: new Date(d), name: src.name, amt, sourceId: src.id, type: src.type });
      }
    };

    if (src.freq === 'weekly' || src.freq === 'biweekly') {
      const stepDays = src.freq === 'weekly' ? 7 : 14;
      let anchor = new Date(src.firstPayMs || startDate);
      anchor.setHours(0, 0, 0, 0);
      // Slide the anchor to the first occurrence on/after startDate, in either direction.
      if (anchor > startDate) {
        while (anchor.getTime() - stepDays * oneDay >= startDate.getTime()) {
          anchor = new Date(anchor.getTime() - stepDays * oneDay);
        }
      } else {
        while (anchor < startDate) anchor = new Date(anchor.getTime() + stepDays * oneDay);
      }
      for (let d = anchor; d <= endDate; d = new Date(d.getTime() + stepDays * oneDay)) push(d);
    } else if (src.freq === 'semimonthly') {
      // No specific dates are captured for semimonthly sources — assume the
      // common 1st & 15th cadence.
      let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      while (cursor <= endDate) {
        [1, 15].forEach((day) => push(new Date(cursor.getFullYear(), cursor.getMonth(), day)));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      }
    } else if (src.freq === 'monthly') {
      // No specific day is captured for monthly sources — assume the 1st.
      let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      while (cursor <= endDate) {
        push(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      }
    }
    // 'annual' sources are skipped — see doc comment above.
  });

  return events.sort((a, b) => a.date - b.date);
}

/**
 * projectBillEvents — expand fixed bills, card minimums, and subscriptions
 * into dated events within [startDate, endDate], including annual
 * subscriptions whose due date falls in range.
 */
export function projectBillEvents(cards, fixedBills, subscriptions, startDate, endDate) {
  const bills = getAllBills(cards, fixedBills, subscriptions);
  const events = [];

  let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  while (cursor <= endDate) {
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    bills.forEach((b) => {
      const day = Math.min(b.due, daysInMonth);
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
      if (d >= startDate && d <= endDate) {
        events.push({ date: d, name: b.name, amt: b.amt, id: b.id, col: b.col, isSub: !!b.isSub });
      }
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  (subscriptions || [])
    .filter((s) => s.cycle === 'annual' && s.amt > 0)
    .forEach((s) => {
      const dueMonth = (s.dueMonth !== null && s.dueMonth !== undefined ? s.dueMonth : startDate.getMonth() + 1) - 1;
      let d = new Date(startDate.getFullYear(), dueMonth, s.due || 1);
      if (d < startDate) d = new Date(d.getFullYear() + 1, d.getMonth(), d.getDate());
      if (d >= startDate && d <= endDate) {
        events.push({ date: d, name: s.name, amt: s.amt, id: s.id, col: s.col, isSub: true, isAnnual: true });
      }
    });

  return events.sort((a, b) => a.date - b.date);
}

/**
 * getCashFlowForecast — combines projected paydays and bill due dates into a
 * single running-balance timeline, starting from the user's current bank
 * balance. Powers the Cash Flow Forecast dashboard card.
 */
export function getCashFlowForecast(state, days = 45) {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDate = new Date(startDate.getTime() + days * 86400000);

  const incomeEvents = projectIncomeEvents(state.incomeSources, startDate, endDate).map((e) => ({
    ...e,
    kind: 'income',
  }));
  const billEvents = projectBillEvents(
    state.cards,
    state.fixedBills,
    state.subscriptions,
    startDate,
    endDate
  ).map((e) => ({ ...e, kind: 'bill' }));

  // Income posted on the same day as a bill is applied first, so the running
  // balance reflects the best case rather than a false dip.
  const combined = [...incomeEvents, ...billEvents].sort((a, b) => {
    const dt = a.date - b.date;
    if (dt !== 0) return dt;
    return a.kind === 'income' ? -1 : 1;
  });

  const startingBalance = state.bankBalance || 0;
  let bal = startingBalance;
  const timeline = combined.map((e) => {
    bal += e.kind === 'income' ? e.amt : -e.amt;
    return { ...e, balanceAfter: bal };
  });

  const totalProjectedIncome = incomeEvents.reduce((s, e) => s + e.amt, 0);
  const totalProjectedBills = billEvents.reduce((s, e) => s + e.amt, 0);
  const lowestPoint = timeline.reduce(
    (min, e) => (e.balanceAfter < min.balanceAfter ? e : min),
    { balanceAfter: startingBalance, date: startDate }
  );
  const willGoNegative = timeline.some((e) => e.balanceAfter < 0);

  return {
    startDate,
    endDate,
    days,
    timeline,
    totalProjectedIncome,
    totalProjectedBills,
    netProjected: totalProjectedIncome - totalProjectedBills,
    startingBalance,
    hasBankBalance: startingBalance > 0,
    lowestPoint,
    willGoNegative,
  };
}

/**
 * Scan variable spending history for merchants that appear in 2+ distinct
 * months at a consistent amount (within 10% tolerance). Returns them sorted
 * by frequency then amount, excluding names already tracked as bills/subs.
 */
export function getRecurringCharges(varSpend, fixedBills, subscriptions) {
  const byMerchant = {};
  Object.entries(varSpend || {}).forEach(([moKey, catMap]) => {
    Object.entries(catMap || {}).forEach(([catId, items]) => {
      (items || []).forEach((item) => {
        const key = (item.place || '').toLowerCase().trim();
        if (!key) return;
        if (!byMerchant[key]) byMerchant[key] = [];
        byMerchant[key].push({ ...item, moKey, catId });
      });
    });
  });

  const existingNames = new Set([
    ...(fixedBills || []).map((b) => b.name.toLowerCase().trim()),
    ...(subscriptions || []).map((s) => s.name.toLowerCase().trim()),
  ]);

  const results = [];
  Object.entries(byMerchant).forEach(([key, entries]) => {
    if (existingNames.has(key)) return;

    const months = new Set(entries.map((e) => e.moKey));
    if (months.size < 2) return;

    const amounts = entries.map((e) => e.amt).sort((a, b) => a - b);
    const median = amounts[Math.floor(amounts.length / 2)];
    if (median <= 0) return;
    if (!amounts.every((a) => Math.abs(a - median) / median <= 0.1)) return;

    const latest = [...entries].sort((a, b) => (b.ts || 0) - (a.ts || 0))[0];
    results.push({
      name: latest.place,
      amount: Math.round(median * 100) / 100,
      months: months.size,
      catId: latest.catId,
    });
  });

  return results.sort((a, b) => b.months - a.months || b.amount - a.amount);
}

/**
 * Compare current month vs previous month spending by category.
 * Returns categories sorted by absolute delta, largest first.
 */
export function getMonthOverMonth(varSpend, cats) {
  const now = new Date();
  const curK = mk(now.getFullYear(), now.getMonth());
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevK = mk(prev.getFullYear(), prev.getMonth());
  const curMap = varSpend[curK] || {};
  const prevMap = varSpend[prevK] || {};

  return (cats || [])
    .map((cat) => {
      const cur = catTotal(curMap[cat.id] || []);
      const prv = catTotal(prevMap[cat.id] || []);
      return { cat, cur, prev: prv, delta: cur - prv };
    })
    .filter((r) => r.cur > 0 || r.prev > 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 5);
}

/**
 * getSpendingStory — generates 3-5 plain-English sentences that describe the
 * current month's finances: budget pace, vs-last-month change, top category,
 * savings, and debt progress.  Returns an empty array if there is no data yet.
 */
// ── XP Store ──────────────────────────────────────────────────────────────────

export const STORE_CATALOG = [
  // ── Themes (level-gated, free to equip) ──────────────────────────────────
  { id: 'theme_dark',    cat: 'themes', type: 'theme', name: 'Dark',    em: '🌑', value: 'dark',     xpCost: 0, levelReq: 1,  desc: 'The default dark theme' },
  { id: 'theme_light',   cat: 'themes', type: 'theme', name: 'Light',   em: '☀️', value: 'light',    xpCost: 0, levelReq: 1,  desc: 'Clean light mode' },
  { id: 'theme_mid',     cat: 'themes', type: 'theme', name: 'Midnight', em: '🌙', value: 'midnight', xpCost: 0, levelReq: 10, desc: 'Deep space dark, unlocked at Lv10' },
  { id: 'theme_forest',  cat: 'themes', type: 'theme', name: 'Forest',  em: '🌲', value: 'forest',   xpCost: 0, levelReq: 12, desc: 'Lush green woodland tones' },
  { id: 'theme_ocean',   cat: 'themes', type: 'theme', name: 'Ocean',   em: '🌊', value: 'ocean',    xpCost: 0, levelReq: 14, desc: 'Deep-sea blues' },
  { id: 'theme_sunset',  cat: 'themes', type: 'theme', name: 'Sunset',  em: '🌅', value: 'sunset',   xpCost: 0, levelReq: 16, desc: 'Warm amber and orange hues' },
  { id: 'theme_neon',    cat: 'themes', type: 'theme', name: 'Neon',    em: '⚡', value: 'neon',     xpCost: 0, levelReq: 18, desc: 'Cyberpunk neon glow' },

  // ── Accent colors (XP purchase) ───────────────────────────────────────────
  { id: 'accent_gold',   cat: 'accents', type: 'accent', name: 'Gold',    em: '🌟', value: '#ffd93d', xpCost: 0,   levelReq: 1,  desc: 'The classic gold accent' },
  { id: 'accent_emerald',cat: 'accents', type: 'accent', name: 'Emerald', em: '💚', value: '#2ecc71', xpCost: 150, levelReq: 5,  desc: 'Rich emerald green' },
  { id: 'accent_sky',    cat: 'accents', type: 'accent', name: 'Sky Blue', em: '🩵', value: '#4d96ff', xpCost: 150, levelReq: 5,  desc: 'Crisp sky blue' },
  { id: 'accent_rose',   cat: 'accents', type: 'accent', name: 'Rose',    em: '🌸', value: '#ff6bdf', xpCost: 200, levelReq: 8,  desc: 'Soft rose pink' },
  { id: 'accent_purple', cat: 'accents', type: 'accent', name: 'Purple',  em: '💜', value: '#7C6FF7', xpCost: 250, levelReq: 10, desc: 'Royal purple' },
  { id: 'accent_flame',  cat: 'accents', type: 'accent', name: 'Flame',   em: '🔥', value: '#ff8c42', xpCost: 250, levelReq: 10, desc: 'Fiery orange' },
  { id: 'accent_crimson',cat: 'accents', type: 'accent', name: 'Crimson', em: '❤️', value: '#e74c3c', xpCost: 300, levelReq: 12, desc: 'Deep crimson red' },
  { id: 'accent_teal',   cat: 'accents', type: 'accent', name: 'Teal',    em: '🩵', value: '#1abc9c', xpCost: 300, levelReq: 12, desc: 'Minty teal' },

  // ── Profile titles (level-gated, free) ────────────────────────────────────
  { id: 'title_budgeter', cat: 'titles', type: 'title', name: 'Budgeter',     em: '📊', value: 'Budgeter',      xpCost: 0, levelReq: 5,  desc: 'You track your spending like a pro' },
  { id: 'title_saver',    cat: 'titles', type: 'title', name: 'Super Saver',  em: '🐷', value: 'Super Saver',   xpCost: 0, levelReq: 8,  desc: 'Consistent savings month after month' },
  { id: 'title_fighter',  cat: 'titles', type: 'title', name: 'Debt Fighter', em: '⚔️', value: 'Debt Fighter',  xpCost: 0, levelReq: 10, desc: 'On a mission to destroy debt' },
  { id: 'title_investor', cat: 'titles', type: 'title', name: 'The Investor', em: '📈', value: 'The Investor',  xpCost: 0, levelReq: 13, desc: 'Playing the long game' },
  { id: 'title_mind',     cat: 'titles', type: 'title', name: 'Money Mind',   em: '🧠', value: 'Money Mind',    xpCost: 0, levelReq: 15, desc: 'Financial mastery achieved' },
  { id: 'title_legend',   cat: 'titles', type: 'title', name: 'Legend',       em: '👑', value: 'Legend',        xpCost: 0, levelReq: 20, desc: 'The pinnacle of financial wisdom' },

  // ── Confetti effects (XP purchase) ────────────────────────────────────────
  { id: 'fx_classic',   cat: 'effects', type: 'confetti', name: 'Classic',      em: '🎊', value: 'classic',  xpCost: 0,   levelReq: 1,  desc: 'Rainbow confetti, the original' },
  { id: 'fx_money',     cat: 'effects', type: 'confetti', name: 'Money Rain',   em: '💵', value: 'money',    xpCost: 200, levelReq: 6,  desc: 'Make it rain with cash emojis' },
  { id: 'fx_stars',     cat: 'effects', type: 'confetti', name: 'Star Shower',  em: '⭐', value: 'stars',    xpCost: 300, levelReq: 9,  desc: 'A shower of golden stars' },
  { id: 'fx_hearts',    cat: 'effects', type: 'confetti', name: 'Hearts',       em: '❤️', value: 'hearts',   xpCost: 350, levelReq: 12, desc: 'Love your budget decisions' },
  { id: 'fx_diamonds',  cat: 'effects', type: 'confetti', name: 'Diamonds',     em: '💎', value: 'diamonds', xpCost: 500, levelReq: 15, desc: 'For the true high rollers' },
];

export function getAvailableXP(state) {
  return Math.max(0, computeXP(state) - (state.xpSpent || 0));
}

export function getEquippedTitle(state) {
  const id = state.storeEquipped?.profileTitle;
  if (!id) return null;
  return STORE_CATALOG.find(i => i.id === id) || null;
}

export function getSpendingStory(state) {
  const now = new Date();
  const curK = mk(now.getFullYear(), now.getMonth());
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevK = mk(prevDate.getFullYear(), prevDate.getMonth());
  const curMos = MOS[now.getMonth()];
  const prevMos = MOS[prevDate.getMonth()];
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  const {
    varSpend = {},
    incomeLog = [],
    paidBills = {},
    savDeposits = {},
    cards = [],
    fixedBills = [],
    subscriptions = [],
    monthlyIncome = 0,
    incomeMode = 'monthly',
    savContrib = 0,
    budgetSlider = 50,
    customCats = [],
    disabledCats = [],
  } = state;

  const cats = getAllCats(customCats, disabledCats);
  const allBills = getAllBills(cards, fixedBills, subscriptions);
  const curVar = moVar(varSpend, curK);
  const prevVar = moVar(varSpend, prevK);
  const curInc = incomeMode === 'monthly' ? (monthlyIncome || 0) : moIncome(incomeLog, curK);
  const curSav = moSavings(savDeposits, curK, savContrib);
  const { budget: varBudget } = getVarBudget(curInc, allBills, curSav, budgetSlider ?? 50);

  const sentences = [];

  // 1. Budget pace (only meaningful from day 5 onward)
  if (curInc > 0 && curVar > 0 && dayOfMonth >= 5) {
    const projected = Math.round((curVar / dayOfMonth) * daysInMonth);
    if (varBudget > 0) {
      if (projected <= varBudget) {
        sentences.push(
          `📈 You're on pace to finish ${curMos} ${fc(varBudget - projected)} under your discretionary budget.`
        );
      } else {
        sentences.push(
          `⚠️ At your current pace you'll end ${curMos} about ${fc(projected - varBudget)} over your discretionary budget.`
        );
      }
    }
  }

  // 2. vs last month
  if (curVar > 0 && prevVar > 0) {
    const diff = curVar - prevVar;
    const pct = Math.round(Math.abs(diff / prevVar) * 100);
    if (diff > 20) {
      sentences.push(
        `📊 Variable spending is up ${fc(diff)} (${pct}%) compared to ${prevMos}.`
      );
    } else if (diff < -20) {
      sentences.push(
        `📊 Variable spending is down ${fc(Math.abs(diff))} (${pct}%) from ${prevMos} — great job.`
      );
    }
  }

  // 3. Top spending category this month
  const catAmts = cats
    .map((cat) => ({
      cat,
      amt: catTotal((varSpend[curK] || {})[cat.id] || []),
    }))
    .filter((x) => x.amt > 0)
    .sort((a, b) => b.amt - a.amt);

  if (catAmts.length > 0) {
    const top = catAmts[0];
    sentences.push(
      `${top.cat.em} Your biggest spend so far is ${top.cat.name} at ${fc(top.amt)}${
        catAmts.length > 1 ? `, followed by ${catAmts[1].cat.name} (${fc(catAmts[1].amt)})` : ''
      }.`
    );
  }

  // 4. Savings progress
  if (curSav > 0) {
    sentences.push(`💵 You've set aside ${fc(curSav)} in savings this month.`);
  }

  // 5. Debt / bill payments
  const totBal = cards.reduce((s, c) => s + (+c.bal || 0), 0);
  if (totBal > 0) {
    const paidTot = moPaid(paidBills, curK, cards, fixedBills);
    if (paidTot > 0) {
      sentences.push(`💳 ${fc(paidTot)} paid toward fixed bills and debt so far.`);
    }
  }

  return sentences;
}


// ── Paycheck Plan (Autopilot) ────────────────────────────────────────────────
//
// Splits one paycheck into a default plan the user can review and change:
//   1. bills due before the next paycheck (card minimums included)
//   2. spending money for this pay period
//   3. starter emergency fund
//   4. extra toward high-interest debt
//   5. full emergency fund
//   6. sinking funds and savings goals
//   7. extra toward remaining debt, then anything left over to savings
// Every line carries a one-sentence `reason` so the plan explains itself.

export const PLAN_DEFAULTS = {
  starterEF: 1000, // starter emergency fund, in dollars
  efMonths: 3, // full emergency fund = this many months of bills
  highAprThreshold: 8, // APR % at or above which debt counts as high-interest
};

const r2 = (n) => Math.round(n * 100) / 100;
const DAY_MS = 86400000;

/**
 * Find the user's emergency fund among savings goals or sinking funds by name.
 * @returns {{ id: string|null, name: string, balance: number, source: string|null }}
 */
export function findEmergencyFund(state) {
  const isEF = (n) => /emergency/i.test(n || '');
  const goal = (state.extraGoals || []).find((g) => isEF(g.name));
  if (goal) return { id: goal.id, name: goal.name, balance: +goal.bal || 0, source: 'goal' };
  const fund = (state.sinkingFunds || []).find((f) => isEF(f.name));
  if (fund) return { id: fund.id, name: fund.name, balance: +fund.balance || 0, source: 'sinking' };
  return { id: null, name: 'Emergency Fund', balance: 0, source: null };
}

/**
 * Next paycheck on or after `today`, plus the date of the one after it
 * (the end of this pay period). Returns null when no recurring income
 * sources are set up.
 */
export function getNextPayPeriod(state, today = new Date()) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start.getTime() + 70 * DAY_MS);
  const events = projectIncomeEvents(state.incomeSources, start, end);
  if (!events.length) return null;
  const payDate = events[0].date;
  const sameDay = events.filter((e) => e.date.getTime() === payDate.getTime());
  const next = events.find((e) => e.date > payDate);
  return {
    payDate,
    nextPayDate: next ? next.date : new Date(payDate.getTime() + 30 * DAY_MS),
    amt: r2(sameDay.reduce((s, e) => s + e.amt, 0)),
    sources: sameDay.map((e) => e.name),
  };
}

function orderDebts(cards, method) {
  const list = (cards || []).filter((c) => (+c.bal || 0) > 0.01);
  if (method === 'avalanche') return list.sort((a, b) => (+b.apr || 0) - (+a.apr || 0));
  return list.sort((a, b) => (+a.bal || 0) - (+b.bal || 0));
}

/**
 * planPaycheck — the Autopilot default plan for one paycheck.
 *
 * @param {object} state  app state
 * @param {object} [opts]
 *   today     Date used to find the next paycheck (default: now)
 *   period    { payDate, nextPayDate, amt } to plan instead of the next paycheck
 *   settings  overrides for PLAN_DEFAULTS
 * @returns {null | {
 *   payDate, nextPayDate, amt, items: Array<{ key, label, amt, reason, targetId? }>,
 *   billsDue: Array, shortfall: number, monthlyIncome: number
 * }}
 */
export function planPaycheck(state, opts = {}) {
  const period = opts.period || getNextPayPeriod(state, opts.today || new Date());
  if (!period || !(period.amt > 0)) return null;
  const cfg = { ...PLAN_DEFAULTS, ...(state.planSettings || {}), ...(opts.settings || {}) };
  const { payDate, nextPayDate, amt } = period;

  const monthlyIncome = sourcesToMonthly(state.incomeSources) || +state.monthlyIncome || 0;
  const share = monthlyIncome > 0 ? Math.min(1, amt / monthlyIncome) : 1;
  const items = [];
  let left = amt;
  const take = (key, label, want, reason, extra = {}) => {
    const a = r2(Math.max(0, Math.min(left, want)));
    if (a <= 0) return 0;
    left = r2(left - a);
    items.push({ key, label, amt: a, reason, ...extra });
    return a;
  };

  // 1 — bills due before the next paycheck (card minimums included)
  const billsDue = projectBillEvents(
    state.cards,
    state.fixedBills,
    state.subscriptions,
    payDate,
    new Date(nextPayDate.getTime() - DAY_MS)
  );
  const billsTotal = r2(billsDue.reduce((s, b) => s + (+b.amt || 0), 0));
  const shortfall = r2(Math.max(0, billsTotal - amt));
  if (billsTotal > 0) {
    take(
      'bills',
      'Bills due before next payday',
      billsTotal,
      `${billsDue.length} bill${billsDue.length === 1 ? '' : 's'} come out before your next paycheck, so this is set aside first.`
    );
  }

  // 2 — spending money for this pay period
  const catTotalBudget = Object.values(state.catBudgets || {}).reduce((s, v) => s + (+v || 0), 0);
  const slider = state.budgetSlider ?? 50;
  const spendWant =
    catTotalBudget > 0 ? catTotalBudget * share : left * (Math.max(0, Math.min(100, slider)) / 100);
  take(
    'spending',
    'Spending money',
    spendWant,
    catTotalBudget > 0
      ? 'Your category budgets (groceries, gas and so on) for this pay period.'
      : `Your budget style keeps ${slider}% of what's left after bills for everyday spending.`
  );

  // 3 — starter emergency fund
  const ef = findEmergencyFund(state);
  let efBal = ef.balance;
  if (efBal < cfg.starterEF) {
    efBal += take(
      'ef_starter',
      ef.name,
      cfg.starterEF - efBal,
      `A starter cushion of ${f0(cfg.starterEF)} keeps a surprise bill from going on a credit card.`,
      { targetId: ef.id }
    );
  }

  // 4 — extra toward high-interest debt
  const debts = orderDebts(state.cards, state.method);
  const highApr = debts.filter((c) => (+c.apr || 0) >= cfg.highAprThreshold);
  const extraDebt = {};
  highApr.forEach((c) => {
    const room = (+c.bal || 0) - (+c.min || 0) - (extraDebt[c.id] || 0);
    const a = take(
      'debt_extra',
      `Extra to ${c.name}`,
      room,
      `${c.name} charges ${+c.apr}% APR, so extra dollars here save the most interest.`,
      { targetId: c.id }
    );
    extraDebt[c.id] = (extraDebt[c.id] || 0) + a;
  });

  // 5 — full emergency fund
  const monthlyBills = getAllBills(state.cards, state.fixedBills, state.subscriptions).reduce(
    (s, b) => s + (+b.amt || 0),
    0
  );
  const efFull = Math.max(cfg.starterEF, monthlyBills * cfg.efMonths);
  if (efBal < efFull) {
    efBal += take(
      'ef_full',
      ef.name,
      efFull - efBal,
      `Building toward ${cfg.efMonths} months of bills (${f0(efFull)}) so a job loss or move doesn't set you back.`,
      { targetId: ef.id }
    );
  }

  // 6 — sinking funds and savings goals
  (state.sinkingFunds || [])
    .filter((f) => f.id !== ef.id && (+f.target || 0) > (+f.balance || 0))
    .forEach((f) => {
      const need = (+f.target || 0) - (+f.balance || 0);
      const per = (+f.monthly || 0) > 0 ? (+f.monthly || 0) * share : need;
      take('fund', f.name, Math.min(need, per), `Your planned set-aside for ${f.name}.`, {
        targetId: f.id,
      });
    });
  const goals = (state.extraGoals || []).filter(
    (g) => g.id !== ef.id && g.type !== 'account' && (+g.target || 0) > (+g.bal || 0)
  );
  const goalNeed = goals.reduce((s, g) => s + (+g.target - (+g.bal || 0)), 0);
  const pool = left;
  goals.forEach((g) => {
    const need = +g.target - (+g.bal || 0);
    take(
      'goal',
      g.name,
      Math.min(need, (pool * need) / goalNeed),
      `Split across your goals by how much each one still needs.`,
      { targetId: g.id }
    );
  });

  // 7 — remaining debt, then savings
  debts
    .filter((c) => !highApr.includes(c))
    .forEach((c) => {
      take(
        'debt_extra',
        `Extra to ${c.name}`,
        (+c.bal || 0) - (+c.min || 0),
        `Everything else is covered, so this pays ${c.name} off sooner.`,
        { targetId: c.id }
      );
    });
  take('savings', 'Extra savings', left, 'Everything is covered, so the rest goes to savings.');

  return { payDate, nextPayDate, amt, items, billsDue, billsTotal, shortfall, monthlyIncome };
}

/**
 * projectFinances — month-by-month simulation that runs the Autopilot plan
 * forward. Each month: income comes in, planPaycheck splits it, debts accrue
 * interest and take their minimum plus any extra, savings buckets fill, and
 * a paid-off debt's minimum frees up for the next priority.
 *
 * @param {object} state
 * @param {number} [months=60]
 * @param {Date}   [start]  first month simulated (default: next month)
 * @returns {null | {
 *   points: Array<{ i, date, lbl, debt, saved, netWorth }>,
 *   milestones: Array<{ key, label, date, months }>,
 *   monthlyIncome: number
 * }}
 */
export function projectFinances(state, months = 60, start) {
  const monthlyIncome = sourcesToMonthly(state.incomeSources) || +state.monthlyIncome || 0;
  if (monthlyIncome <= 0) return null;
  const now = new Date();
  const first = start || new Date(now.getFullYear(), now.getMonth() + 1, 1);

  // Working copies the simulation can change
  const cards = (state.cards || []).map((c) => ({ ...c, bal: +c.bal || 0, min: +c.min || 0 }));
  const extraGoals = (state.extraGoals || []).map((g) => ({ ...g, bal: +g.bal || 0 }));
  const sinkingFunds = (state.sinkingFunds || []).map((f) => ({ ...f, balance: +f.balance || 0 }));
  if (!findEmergencyFund(state).id) {
    extraGoals.push({ id: '__ef', name: 'Emergency Fund', bal: 0, target: 0, type: 'goal' });
  }
  const sim = { ...state, cards, extraGoals, sinkingFunds };

  const startNW = getNetWorth(state).netWorth;
  const startDebt = cards.reduce((s, c) => s + c.bal, 0);
  let saved = 0;
  const points = [];
  const hit = {};
  const mark = (key, label, i, date) => {
    if (!hit[key]) hit[key] = { key, label, date, months: i + 1 };
  };

  for (let i = 0; i < months; i++) {
    const date = new Date(first.getFullYear(), first.getMonth() + i, 1);
    const plan = planPaycheck(sim, {
      period: {
        payDate: date,
        nextPayDate: new Date(date.getFullYear(), date.getMonth() + 1, 1),
        amt: monthlyIncome,
      },
    });
    const extra = {};
    let monthSaved = 0;
    (plan ? plan.items : []).forEach((it) => {
      if (it.key === 'debt_extra') extra[it.targetId] = (extra[it.targetId] || 0) + it.amt;
      else if (['ef_starter', 'ef_full', 'goal', 'fund', 'savings'].includes(it.key)) {
        monthSaved += it.amt;
        const g = extraGoals.find((x) => x.id === it.targetId);
        const f = sinkingFunds.find((x) => x.id === it.targetId);
        if (g) g.bal += it.amt;
        else if (f) f.balance += it.amt;
      }
    });
    saved += monthSaved;
    cards.forEach((c) => {
      if (c.bal <= 0.01) return;
      const owed = c.bal * (1 + (+c.apr || 0) / 1200);
      const pay = c.min + (extra[c.id] || 0);
      saved += Math.max(0, pay - owed); // a final payment larger than the balance is kept
      c.bal = Math.max(0, owed - pay);
      if (c.bal <= 0.01) {
        c.bal = 0;
        c.min = 0; // freed up for the next priority
        mark(`paid:${c.id}`, `${c.name} paid off`, i, date);
      }
    });

    const debt = cards.reduce((s, c) => s + c.bal, 0);
    if (startDebt > 0 && debt === 0) mark('debtfree', 'Debt-free', i, date);
    const ef = findEmergencyFund(sim);
    const monthlyBills = getAllBills(cards, state.fixedBills, state.subscriptions).reduce(
      (s, b) => s + (+b.amt || 0),
      0
    );
    const cfg = { ...PLAN_DEFAULTS, ...(state.planSettings || {}) };
    if (ef.balance >= Math.max(cfg.starterEF, monthlyBills * cfg.efMonths) - 0.01) {
      mark('ef', 'Emergency fund full', i, date);
    }
    extraGoals
      .filter((g) => g.id !== ef.id && +g.target > 0 && g.bal >= +g.target - 0.01)
      .forEach((g) => mark(`goal:${g.id}`, `${g.name} reached`, i, date));

    points.push({
      i,
      date,
      lbl: MOS[date.getMonth()] + " '" + String(date.getFullYear()).slice(2),
      debt: r2(debt),
      saved: r2(saved),
      netWorth: r2(startNW + saved + (startDebt - debt)),
    });
  }

  return {
    points,
    milestones: Object.values(hit).sort((a, b) => a.months - b.months),
    monthlyIncome,
  };
}

// ─── Fair leaderboard score ──────────────────────────────────────────────────
// Every part is a ratio (savings rate, % of goals reached, months on budget),
// so someone earning $30k can out-rank someone earning $150k.

/** Average progress (0–1) across savings goals, sinking funds and debt paid down since setup. */
export function getGoalProgress(state) {
  const parts = [];
  (state.extraGoals || []).forEach((g) => {
    if (g.type !== 'account' && g.target > 0) parts.push((g.bal || 0) / g.target);
  });
  (state.sinkingFunds || []).forEach((f) => {
    if (f.target > 0) parts.push((f.balance || 0) / f.target);
  });
  const startDebt = (state.initCards || []).reduce((s, c) => s + (+c.bal || 0), 0);
  if (startDebt > 0) {
    const nowDebt = (state.cards || []).reduce((s, c) => s + (+c.bal || 0), 0);
    parts.push(1 - nowDebt / startDebt);
  }
  if (!parts.length) return 0;
  const clamp = (x) => Math.max(0, Math.min(1, x));
  return parts.reduce((s, x) => s + clamp(x), 0) / parts.length;
}

/**
 * Public stats for the leaderboard. savingsRate is what was actually saved this
 * month (deposits ÷ income), falling back to last month early in a month.
 */
export function getSocialScore(state, { streak = 0, xp = 0, budgetAdherence = 0 }, now = new Date()) {
  const rateFor = (d) => {
    const k = mk(d.getFullYear(), d.getMonth());
    const inc = moIncome(state.incomeLog || [], k) || state.monthlyIncome || sourcesToMonthly(state.incomeSources || []);
    return inc > 0 ? Math.min(1, moSavings(state.savDeposits || {}, k) / inc) : 0;
  };
  const savingsRate = Math.max(rateFor(now), rateFor(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
  const goalProgress = getGoalProgress(state);
  const rankScore = Math.round(
    Math.min(1, savingsRate / 0.2) * 30 +
    budgetAdherence * 25 +
    goalProgress * 25 +
    Math.min(1, streak / 12) * 10 +
    Math.min(1, xp / 5000) * 10
  );
  return { savingsRate, goalProgress, rankScore };
}

// ── Weekly recap ────────────────────────────────────────────────────────────
// Monday 00:00 local time of the week containing `d`.
export function weekStart(d) {
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  return s;
}

const itemTime = (x) =>
  x?.ts ? x.ts : x?.date ? new Date(x.date + 'T12:00:00').getTime() : null;
const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Summarises last full week (Mon–Sun) for whoever the user is, using only
// their own logged data. Returns null when there is nothing to say.
export function getWeeklyRecap(state, now = new Date(), varBudget = 0) {
  const end = weekStart(now); // this Monday, exclusive
  const start = new Date(end); start.setDate(start.getDate() - 7);
  const prevStart = new Date(start); prevStart.setDate(prevStart.getDate() - 7);
  const [s0, s1, s2] = [prevStart.getTime(), start.getTime(), end.getTime()];

  const cats = getAllCats(state.customCats, state.disabledCats);
  const catName = (id) => {
    const c = cats.find((x) => x.id === id) || VCATS.find((x) => x.id === id);
    return c ? `${c.em || ''} ${c.name}`.trim() : id;
  };

  // Spending items live in varSpend[monthKey][catId]; only the months that
  // overlap the two weeks need scanning.
  const keys = new Set();
  for (let t = s0; t < s2; t += 86400000) { const d = new Date(t); keys.add(mk(d.getFullYear(), d.getMonth())); }
  let spent = 0, prevSpent = 0, count = 0;
  const byCat = {}, byPlace = {};
  keys.forEach((k) => {
    const vd = state.varSpend?.[k] || {};
    Object.entries(vd).forEach(([catId, items]) => {
      (Array.isArray(items) ? items : []).forEach((x) => {
        const t = itemTime(x); const amt = Number(x.amt) || 0;
        if (t == null || amt <= 0) return;
        if (t >= s1 && t < s2) {
          spent += amt; count++;
          byCat[catId] = (byCat[catId] || 0) + amt;
          const p = (x.place || '').trim();
          if (p) byPlace[p.toLowerCase()] = { name: p, amt: (byPlace[p.toLowerCase()]?.amt || 0) + amt, n: (byPlace[p.toLowerCase()]?.n || 0) + 1 };
        } else if (t >= s0 && t < s1) prevSpent += amt;
      });
    });
  });

  const income = (state.incomeLog || []).reduce((s, e) => {
    const t = itemTime({ date: e.date }); return t >= s1 && t < s2 ? s + (e.amt || 0) : s;
  }, 0);
  const saved = Object.values(state.savDeposits || {}).flat().reduce((s, d) => {
    const t = itemTime(d); return t != null && t >= s1 && t < s2 ? s + (d.amt || 0) : s;
  }, 0);
  const startDay = isoDay(start), endDay = isoDay(new Date(s2 - 1));
  const noSpend = (state.noSpendDays || []).filter((d) => d >= startDay && d <= endDay).length;

  if (!count && !income && !saved && !noSpend) return null;

  const topCatEntry = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];
  const topPlace = Object.values(byPlace).sort((a, b) => b.amt - a.amt)[0] || null;
  const frequent = Object.values(byPlace).sort((a, b) => b.n - a.n || b.amt - a.amt)[0] || null;
  // Weekly share of the monthly fun-money budget, so the target adapts to the user.
  const weekBudget = varBudget > 0 ? (varBudget * 12) / 52 : 0;
  const change = prevSpent > 0 ? (spent - prevSpent) / prevSpent : null;

  const wins = [], watch = [];
  if (weekBudget && spent <= weekBudget) wins.push(`Stayed ${f0(weekBudget - spent)} under your weekly budget`);
  if (change != null && change <= -0.1) wins.push(`Spent ${Math.round(-change * 100)}% less than the week before`);
  if (saved > 0) wins.push(`Put ${f0(saved)} toward savings`);
  if (noSpend > 0) wins.push(`${noSpend} no-spend day${noSpend > 1 ? 's' : ''}`);
  if (weekBudget && spent > weekBudget) watch.push(`${f0(spent - weekBudget)} over your weekly budget`);
  if (change != null && change >= 0.25 && spent - prevSpent >= 20) watch.push(`Spending up ${Math.round(change * 100)}% on the week before`);
  if (frequent && frequent.n >= 3) watch.push(`${frequent.n} trips to ${frequent.name} (${f0(frequent.amt)})`);

  return {
    weekKey: `wk_${startDay}`,
    start, end: new Date(s2 - 1),
    spent, prevSpent, change, count,
    income, saved, noSpend, weekBudget,
    topCat: topCatEntry ? { id: topCatEntry[0], name: catName(topCatEntry[0]), amt: topCatEntry[1] } : null,
    topPlace,
    wins, watch,
  };
}
